<template>
  <EditDialog
    ref="EditDialog"
    :title="title"
    :has-title-suffix="false"
    :display-delete-button="false"
    fullscreen
    :disable-close-key="true"
    :loading-save="loading.save"
    :loading-delete="loading.delete"
  >
    <template #body>
      <v-layout justify-center row fill-height wrap>
        <v-flex xs12>
          <AlertBox ref="errorMsg" />

          <v-card-text>
            <!-- はじめに -->
            <p class="body-1 mb-6">
              株式会社合通トラスコ（以下、当社）ではパートナー社で働かれる皆さま（以下、皆さま）への業務連絡を円滑に行うために、連絡手段として、皆さまが個人でご利用中のLINE（LINE株式会社が提供するコミュニケーションアプリ）を利用したサービスを行っています。本利用規約の内容をよくお読みいただき、ご確認のうえ、同意頂ける場合は「同意する」ボタンを押して先に進んでください。
            </p>

            <!-- 各セクション -->
            <div>
              <!-- 取得する情報 -->
              <section class="mb-5">
                <h2 class="title font-weight-bold mb-3">【取得する情報】</h2>
                <div class="pl-4">
                  <h3 class="subtitle-1 font-weight-bold">LINEアカウントID</h3>
                  <p class="body-2 grey--text text--darken-1">
                    LINEプラットフォームから自動的に発行される、ご自身のLINEアカウントを識別するための固有のID
                  </p>
                </div>
              </section>

              <!-- 利用目的 -->
              <section class="mb-5">
                <h2 class="title font-weight-bold mb-3">【利用目的】</h2>
                <p class="body-1 mb-3 pl-4">
                  当社は、取得した情報を以下の目的のためにのみ利用し、本人の同意なくこれ以外の目的で利用することはありません。
                </p>
                <div class="pl-4">
                  <div class="mb-2">
                    <h3 class="subtitle-1 font-weight-bold">本人認証およびアカウント連携</h3>
                    <p class="body-2 grey--text text--darken-1">
                      皆さまのLINEアカウントを識別し、ご本人であることを確認するため。
                    </p>
                  </div>
                  <div class="mb-2">
                    <h3 class="subtitle-1 font-weight-bold">業務連絡および通知の送信</h3>
                    <p class="body-2 grey--text text--darken-1">
                      当社から、運行情報、配車指示、緊急連絡、その他業務上必要なお知らせを皆さまのLINEアカウントへ送信するため。
                    </p>
                  </div>
                  <div>
                    <h3 class="subtitle-1 font-weight-bold">円滑なサービス提供と改善</h3>
                    <p class="body-2 grey--text text--darken-1">
                      利用状況を分析し、サービスの利便性向上や改善、および利用者からのお問い合わせに対応するため。
                    </p>
                  </div>
                </div>
              </section>

              <!-- 個人情報の取り扱い -->
              <section class="mb-5">
                <h2 class="title font-weight-bold mb-3">【個人情報の取り扱い】</h2>
                <div class="pl-4 body-1">
                  <p>取得した情報は、上記の利用目的に限り使用し、その他の目的には一切使用しません。</p>
                  <p>
                    当社の個人情報保護方針(
                    <a
                      href="https://www.trasco.co.jp/corporate/privacypolicy/n-about.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://www.trasco.co.jp/corporate/privacypolicy/n-about.html
                    </a>
                    )に従い、適切に管理・保護いたします。
                  </p>
                </div>
              </section>

              <!-- 同意の任意性 -->
              <section class="mb-5">
                <h2 class="title font-weight-bold mb-3">【同意の任意性】</h2>
                <div class="pl-4 body-1">
                  <p class="mb-2">
                    LINEでの連絡は皆さまの同意を前提としており、同意されない場合でも一切の不利益を被ることはありません。
                  </p>
                  <p class="mb-2">
                    使用を希望されない場合は、他の連絡手段（電話・メールなど）をご案内いたしますのでお申し出ください。
                  </p>
                  <p>
                    一度同意した後でも、いつでも同意を撤回することができます。撤回をご希望の場合は担当者までお申し出ください。
                  </p>
                </div>
              </section>

              <!-- LINE連携に関する基本ルール -->
              <section class="mb-5">
                <h2 class="title font-weight-bold mb-3">【LINE連携に関する基本ルール】</h2>
                <div class="pl-4 body-1">
                  <p class="mb-2">
                    業務連絡は原則として当社が定める業務時間内で行います。ただし、緊急時を除きます。
                  </p>
                  <p>
                    休日・深夜の連絡に対する返信は、翌営業日・翌就業日で構いません。プライベートな内容の連絡はお控えください。※緊急連絡については可能な範囲での確認・対応をお願いいたしますが、対応を義務付けるものではありません。
                  </p>
                </div>
              </section>

              <!-- 注意事項 -->
              <section>
                <h2 class="title font-weight-bold mb-3">【注意事項】</h2>
                <div class="pl-4 body-1">
                  <p class="mb-3">
                    本件は、LINE株式会社が提供するサービスを利用して当社とやりとりを行うことを意味し、LINE社による情報の収集・利用に関しては、同社のプライバシーポリシー（<a
                      href="https://www.lycorp.co.jp/ja/"
                      target="_blank"
                      rel="noopener noreferrer"
                      >https://www.lycorp.co.jp/ja/</a
                    >）をご確認ください。
                  </p>
                  <p class="mb-3">
                    LINE株式会社のサービス提供停止、仕様変更、障害その他当該サービスに起因して発生したいかなる損害についても、当社は一切の責任を負いかねます。
                  </p>
                  <p class="mb-3">
                    本サービスを通じて皆様が取得される情報は、当社の重要な機密情報です。これらを業務以外の目的で利用したり、第三者へ開示・複製・配布することは、契約違反および不正行為に該当し、固く禁じます。違反が確認された場合には、法的措置を含む厳正な対応を行うことがあります。
                  </p>
                </div>
              </section>
            </div>
          </v-card-text>

          <v-checkbox
            v-model="checked"
            label="利用規約に同意する"
            :disabled="!!getTokenExpired()"
            class="checkbox"
          />

          <div class="spacer"></div>
        </v-flex>
      </v-layout>
    </template>
    <template #footer>
      <v-btn
        style="margin: 0 auto"
        color="primary"
        :disabled="checked ? false : 'disabled'"
        @click="onClickAgree"
        >アカウント連携を開始する
      </v-btn>
    </template>
  </EditDialog>
</template>

<script>
import EditDialog from "@/components/common/dialog/EditDialog";

import ApiHandler from "../../mixin/ApiHandler";
import EditForm from "../../mixin/EditForm";
import AlertBox from "@/components/common/AlertBox.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  components: {
    AlertBox,
    EditDialog
  },
  mixins: [ApiHandler, EditForm],
  data() {
    return {
      title: "LINEによる業務連絡に関する利用規約",
      checked: false,
      agreementContext: "20250715"
    };
  },
  methods: {
    ...mapActions("line", {
      fetchLinkTokenExpired: "fetchLinkTokenExpired"
    }),
    ...mapMutations("line", {
      writeLinkToken: "writeLinkToken"
    }),
    ...mapGetters("line", {
      getTokenExpired: "getTokenExpired"
    }),
    async openDialog(item) {
      await this.checkLinkToken();
      this.initForm(item);
      this.$refs.EditDialog.openDialog();
    },
    closeDialog() {
      this.$refs.EditDialog.closeDialog();
    },
    onClickAgree() {
      this.$emit("agree", this.agreementContext);
      this.closeDialog();
    },
    displayErrorMessage(message) {
      this.$refs.errorMsg.displayMessage(message);
    },
    checkLinkToken() {
      return this.fetchLinkTokenExpired().catch(errorMessage => {
        this.displayErrorMessage(errorMessage);
      });
    }
  }
};
</script>

<style lang="stylus" scoped>
.privacy-policy-text
  overflow-y: scroll
  max-height: calc(100vh - 200px)
  border 1px solid #ccc
  padding 8px

.checkbox
  ::v-deep .v-input__control
    margin: 0 auto;

.spacer
  margin-bottom 70px
</style>
